/**
 * @fileoverview Invoice Number Generation Utility
 *
 * ⚠️ STANDARDIZED SOURCE OF TRUTH FOR INVOICE NUMBER GENERATION ⚠️
 * ===============================================================
 *
 * THIS IS THE CANONICAL METHOD FOR GENERATING INVOICE NUMBERS
 * ACROSS THE ENTIRE APPLICATION.
 *
 * Format: INV-XXX-XXX
 * Example: INV-234-567, INV-890-123
 *
 * Generation Method:
 * - Uses last 6 digits of Date.now() timestamp
 * - Split into two 3-digit groups with hyphen
 * - Ensures uniqueness through timestamp-based generation
 * - Chronologically sortable within same millisecond
 *
 * WHY THIS FORMAT:
 * 1. ✅ Human-readable and easy to communicate (e.g., "INV-234-567")
 * 2. ✅ Short and memorable (11 characters total)
 * 3. ✅ Unique within reasonable time windows (timestamp-based)
 * 4. ✅ No database lookups required
 * 5. ✅ Works across distributed systems
 * 6. ✅ Compatible with PayPal's invoice number requirements
 *
 * USAGE ACROSS APPLICATION:
 * - ✅ Invoice creation forms (InvoiceFormPayPal)
 * - ✅ Service history tables (ServiceHistoryTable)
 * - ✅ PayPal invoice integration (usePayPalInvoices)
 * - ✅ Backend API endpoints (paypal.py)
 * - ✅ Any invoice number generation
 *
 * DO NOT CREATE ALTERNATIVE FORMATS WITHOUT UPDATING THIS FILE FIRST!
 *
 * @module invoiceNumber
 */

/**
 * @function generateInvoiceNumber
 * @description Generate standardized invoice number in INV-XXX-XXX format
 *
 * This is the ONLY function that should be used to generate invoice numbers
 * throughout the application. Do not implement custom generation logic elsewhere.
 *
 * @returns {string} Invoice number in format INV-XXX-XXX
 *
 * @example
 * const invoiceNumber = generateInvoiceNumber();
 * // Returns: "INV-234-567"
 *
 * @example
 * // ✅ CORRECT - Use this function
 * const invoice = {
 *   invoiceNumber: generateInvoiceNumber(),
 *   clientId: '...',
 *   // ... other fields
 * };
 *
 * @example
 * // ❌ WRONG - Don't create custom logic
 * const invoiceNumber = `INV-${Date.now()}`; // WRONG FORMAT!
 * const invoiceNumber = `INV-${Math.random()}`; // NON-STANDARD!
 */
export function generateInvoiceNumber(): string {
  // Use last 6 digits of timestamp for uniqueness
  const sixDigits = String(Date.now()).slice(-6).padStart(6, '0');

  // Split into INV-XXX-XXX format
  const invoiceNumber = `INV-${sixDigits.slice(0, 3)}-${sixDigits.slice(3)}`;

  return invoiceNumber;
}

/**
 * @function validateInvoiceNumber
 * @description Validate invoice number matches standard format
 *
 * @param {string} invoiceNumber - Invoice number to validate
 * @returns {boolean} True if valid INV-XXX-XXX format
 *
 * @example
 * validateInvoiceNumber('INV-234-567'); // true
 * validateInvoiceNumber('INV-1234-567'); // false (too many digits)
 * validateInvoiceNumber('INV234567'); // false (missing hyphens)
 */
export function validateInvoiceNumber(invoiceNumber: string): boolean {
  // Format: INV-XXX-XXX (exactly 3 digits, hyphen, 3 digits)
  const pattern = /^INV-\d{3}-\d{3}$/;
  return pattern.test(invoiceNumber);
}

/**
 * @function formatInvoiceNumber
 * @description Format raw digits into INV-XXX-XXX format
 *
 * Useful for converting user input or migrating legacy formats
 *
 * @param {string | number} digits - 6 digits to format
 * @returns {string} Formatted invoice number
 *
 * @example
 * formatInvoiceNumber('234567'); // "INV-234-567"
 * formatInvoiceNumber(234567); // "INV-234-567"
 */
export function formatInvoiceNumber(digits: string | number): string {
  const sixDigits = String(digits).padStart(6, '0').slice(0, 6);
  return `INV-${sixDigits.slice(0, 3)}-${sixDigits.slice(3)}`;
}
